import { useEffect } from 'react';

import { Skeleton, Stack, Text, useColorModeValue } from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';

import { CompareDatasetCard, DatasetRef } from 'components/shared/Compare';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import { useResolveCompareDataset } from './useResolveCompareDataset';

type Props = {
  /** Named `datasetRef` rather than `ref`, which React treats specially and would strip. */
  datasetRef: DatasetRef;
};

/**
 * Renders a single comparison card for a dataset from the URL. Prefers the rich dataset staged in the
 * compare context (instant, with impact score + process badge); on a cold load / shared link it
 * fetches by ref instead.
 */
const ComparisonCard = ({ datasetRef }: Props) => {
  const grayText = useColorModeValue('gray.500', 'gray.400');
  const { dataset, isLoading } = useResolveCompareDataset(datasetRef);

  const hasFailed = !dataset && !isLoading;

  useEffect(() => {
    if (!hasFailed) return;
    mixpanel.track(MIXPANEL_EVENTS.COMPARISON_DATASET_LOAD_FAILED, {
      dataset_id: datasetRef.id,
      dataset_version: datasetRef.version,
      dataset_system_model: datasetRef.systemModel,
    });
  }, [hasFailed, datasetRef.id, datasetRef.version, datasetRef.systemModel]);

  if (dataset) return <CompareDatasetCard dataset={dataset} />;

  if (isLoading) return <Skeleton height="180px" borderRadius="lg" />;

  return (
    <Stack borderWidth="1px" borderRadius="lg" p={4} justify="center" minH="180px">
      <Text color={grayText}>
        Dataset {datasetRef.id} could not be loaded from {datasetRef.version}-{datasetRef.systemModel}.
      </Text>
    </Stack>
  );
};

export default ComparisonCard;
